import {
  allowedTransitions,
  isIrreversible,
  statusLabel,
  transitionLabel,
  typeLabel,
  type Declaration,
  type DeclarationStatus,
  type DeclarationTransition,
} from '@pedigreesmart/core'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import './declarations.css'

const PER_PAGINA = 25

/** Gli stati, nell'ordine del percorso. Gli ultimi due sono conclusioni. */
const STATI: readonly DeclarationStatus[] = [
  'draft',
  'submitted',
  'review',
  'approved',
  'rejected',
  'registered',
]

/**
 * Le denunce.
 *
 * È la sezione con più regole del gestionale, e l'unica in cui un'azione
 * dell'interfaccia **cambia il registro**: `register` crea i soggetti, chiude
 * e apre le intestazioni, assegna i numeri. Tutto il resto sono passaggi di
 * stato reversibili.
 *
 * I pulsanti mostrati dipendono dallo stato, ma non è quello a decidere: il
 * server rifiuta comunque una transizione non ammessa con un `409` che nomina
 * lo stato attuale. Nasconderli serve solo a non far cliccare qualcosa che
 * darà errore — utile soprattutto quando due persone in segreteria lavorano
 * sulla stessa denuncia e una l'ha già fatta avanzare.
 */
export function DeclarationList({ onNuova }: { onNuova?: (() => void) | undefined }) {
  const { resources } = useSession()
  const [stato, setStato] = useState<DeclarationStatus | ''>('')
  const [pagina, setPagina] = useState(1)

  const query = useQuery({
    queryKey: ['declarations', stato, pagina],
    queryFn: ({ signal }) =>
      resources.declarations.list(
        stato
          ? { status: stato, page: pagina, items_per_page: PER_PAGINA }
          : { page: pagina, items_per_page: PER_PAGINA },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

  const pagine = query.data ? Math.max(1, Math.ceil(query.data.totalItems / PER_PAGINA)) : 1

  return (
    <section aria-labelledby="titolo-denunce">
      <div className="elenco__testa">
        <h1 id="titolo-denunce">Denunce</h1>
        <div className="elenco__azioni">
          {query.data && (
            <p className="elenco__conteggio" aria-live="polite">
              {query.data.totalItems === 1 ? '1 denuncia' : `${query.data.totalItems} denunce`}
            </p>
          )}
          {onNuova && (
            <button type="button" onClick={onNuova}>
              Nuova denuncia
            </button>
          )}
        </div>
      </div>

      <label className="elenco__ricerca">
        <span className="solo-lettore">Filtra per stato</span>
        <select
          value={stato}
          onChange={(event) => {
            setStato(event.target.value as DeclarationStatus | '')
            setPagina(1)
          }}
        >
          <option value="">Tutti gli stati</option>
          {STATI.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
      </label>

      {query.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere le denunce.
        </p>
      )}

      {query.isPending && <p className="messaggio">Caricamento…</p>}

      {query.data?.member.length === 0 && (
        <p className="messaggio">
          {stato ? `Nessuna denuncia in stato «${statusLabel(stato)}».` : 'Nessuna denuncia.'}
        </p>
      )}

      {query.data && query.data.member.length > 0 && (
        <ul className="denunce" data-attesa={query.isFetching ? 'si' : undefined}>
          {query.data.member.map((denuncia) => (
            <Riga key={denuncia.id} denuncia={denuncia} />
          ))}
        </ul>
      )}

      {pagine > 1 && (
        <nav className="paginazione" aria-label="Pagine">
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina <= 1}
            onClick={() => {
              setPagina((p) => p - 1)
            }}
          >
            Precedente
          </button>
          <span aria-live="polite">
            Pagina {pagina} di {pagine}
          </span>
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina >= pagine}
            onClick={() => {
              setPagina((p) => p + 1)
            }}
          >
            Successiva
          </button>
        </nav>
      )}
    </section>
  )
}

function Riga({ denuncia }: { denuncia: Declaration }) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)
  const [violazioni, setViolazioni] = useState<readonly string[]>([])
  const [conferma, setConferma] = useState<DeclarationTransition | null>(null)
  const [motivo, setMotivo] = useState<string | null>(null)

  const applica = useMutation({
    mutationFn: ({ transizione, motivo }: { transizione: DeclarationTransition; motivo?: string }) =>
      resources.declarations.transition(denuncia.id, transizione, motivo),
    onMutate: () => {
      setErrore(null)
      setViolazioni([])
    },
    onSuccess: () => {
      setConferma(null)
      setMotivo(null)
      void cache.invalidateQueries({ queryKey: ['declarations'] })
      // Il registro può essere cambiato: `register` crea soggetti.
      void cache.invalidateQueries({ queryKey: ['animals'] })
    },
    onError: (e: unknown) => {
      const errore = e as { status?: number; violations?: readonly { message: string }[] }

      if (errore.status === 422 && errore.violations) {
        // Le violazioni dicono **cosa manca** per quella transizione: sono
        // l'informazione utile, non il fatto che sia fallita.
        setViolazioni(errore.violations.map((v) => v.message))
        setErrore('La denuncia non è completa per questo passaggio.')

        return
      }

      if (errore.status === 409) {
        setErrore(
          'Lo stato è cambiato nel frattempo: forse qualcun altro l\'ha già fatta avanzare. Ricaricare.',
        )

        return
      }

      setErrore('Non è stato possibile completare l\'operazione.')
    },
  })

  const transizioni = allowedTransitions(denuncia.status)

  return (
    <li className="denuncia">
      <div className="denuncia__testa">
        <div>
          <span className="codice denuncia__numero">{denuncia.number}</span>
          <h2 className="denuncia__tipo">{typeLabel(denuncia.type)}</h2>
          <p className="riga__secondario">
            {denuncia.declarant.display_name} · {data(denuncia.declared_on)}
          </p>
        </div>
        <span className="stato" data-stato={denuncia.status}>
          {statusLabel(denuncia.status)}
        </span>
      </div>

      {denuncia.rejection_reason && (
        <p className="denuncia__motivo">
          <strong>Motivo del rifiuto:</strong> {denuncia.rejection_reason}
        </p>
      )}

      {errore && (
        <div role="alert" className="messaggio messaggio--errore">
          <p>{errore}</p>
          {violazioni.length > 0 && (
            <ul>
              {violazioni.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {transizioni.length > 0 && (
        <div className="denuncia__azioni">
          {transizioni.map((transizione) => (
            <button
              key={transizione}
              type="button"
              data-variante={transizione === 'reject' ? 'piatto' : 'secondario'}
              disabled={applica.isPending}
              onClick={() => {
                if (transizione === 'reject') {
                  // Un `prompt()` del browser è una riga sola, non si rilegge
                  // e qualche browser lo sopprime del tutto. Il motivo del
                  // rifiuto lo legge chi ha presentato la denuncia: merita un
                  // campo vero.
                  setMotivo('')

                  return
                }

                if (isIrreversible(transizione)) {
                  setConferma(transizione)

                  return
                }

                applica.mutate({ transizione })
              }}
            >
              {transitionLabel(transizione)}
            </button>
          ))}
        </div>
      )}

      {motivo !== null && (
        <form
          className="conferma"
          onSubmit={(evento) => {
            evento.preventDefault()

            if (motivo.trim() === '') return

            applica.mutate({ transizione: 'reject', motivo: motivo.trim() })
          }}
        >
          <label className="modulo__campo modulo__campo--largo">
            <span>Motivo del rifiuto</span>
            <textarea
              autoFocus
              rows={3}
              required
              value={motivo}
              onChange={(evento) => {
                setMotivo(evento.target.value)
              }}
            />
          </label>
          <p className="riga__secondario">
            Lo legge chi ha presentato la denuncia: scrivere cosa correggere evita una telefonata.
          </p>
          <div className="denuncia__azioni">
            <button
              type="submit"
              data-variante="secondario"
              disabled={applica.isPending || motivo.trim() === ''}
            >
              {applica.isPending ? 'Invio…' : 'Respingi'}
            </button>
            <button
              type="button"
              data-variante="piatto"
              onClick={() => {
                setMotivo(null)
              }}
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      {conferma && (
        <ConfermaRegistrazione
          id={denuncia.id}
          inCorso={applica.isPending}
          onAnnulla={() => {
            setConferma(null)
          }}
          onConferma={() => {
            applica.mutate({ transizione: conferma })
          }}
        />
      )}
    </li>
  )
}

/**
 * La conferma prima di registrare.
 *
 * Non chiede «sei sicuro?»: chiede all'anteprima **cosa succederà** e lo
 * mostra. È l'unica azione del gestionale che scrive nel registro, e chi la
 * conferma deve poter leggere prima quali soggetti nasceranno e quali
 * intestazioni cambieranno.
 */
function ConfermaRegistrazione({
  id,
  inCorso,
  onConferma,
  onAnnulla,
}: {
  id: number
  inCorso: boolean
  onConferma: () => void
  onAnnulla: () => void
}) {
  const { resources } = useSession()

  const anteprima = useQuery({
    queryKey: ['declaration-preview', id],
    queryFn: ({ signal }) => resources.declarations.preview(id, signal),
  })

  return (
    <div className="conferma" role="group" aria-label="Conferma della registrazione">
      <h3>Cosa succederà</h3>

      {anteprima.isPending && <p>Verifica in corso…</p>}

      {anteprima.isError && (
        <p role="alert">Non è stato possibile calcolare l'anteprima. Meglio non procedere.</p>
      )}

      {anteprima.data && (
        <>
          {anteprima.data.problems.length > 0 && (
            <ul className="conferma__problemi">
              {anteprima.data.problems.map((problema) => (
                <li key={problema}>{problema}</li>
              ))}
            </ul>
          )}

          {anteprima.data.effects.length > 0 && (
            <ul className="conferma__effetti">
              {anteprima.data.effects.map((effetto) => {
                const testo = descriviEffetto(effetto)

                // La descrizione fa da chiave: gli effetti non hanno un id, e
                // due effetti identici sulla stessa denuncia sarebbero
                // comunque la stessa riga da mostrare.
                return <li key={testo}>{testo}</li>
              })}
            </ul>
          )}

          {anteprima.data.effects.length === 0 && anteprima.data.problems.length === 0 && (
            <p>Nessun effetto sul registro.</p>
          )}
        </>
      )}

      <div className="denuncia__azioni">
        <button type="button" data-variante="piatto" onClick={onAnnulla} disabled={inCorso}>
          Annulla
        </button>
        <button
          type="button"
          onClick={onConferma}
          disabled={inCorso || anteprima.data?.ready !== true}
        >
          {inCorso ? 'Registrazione…' : 'Registra'}
        </button>
      </div>
    </div>
  )
}

/**
 * Un effetto in parole.
 *
 * La forma degli effetti la decide il backend e cambia col tipo di denuncia,
 * quindi qui non si presume una struttura: si prova a leggere le chiavi note e
 * si ricade su una descrizione grezza. Meglio una riga poco elegante che una
 * riga vuota davanti a un'azione irreversibile.
 */
function descriviEffetto(effetto: Record<string, unknown>): string {
  const tipo = typeof effetto['type'] === 'string' ? effetto['type'] : null
  const descrizione = typeof effetto['description'] === 'string' ? effetto['description'] : null

  if (descrizione) return descrizione
  if (tipo) return tipo

  return JSON.stringify(effetto)
}

function data(iso: string): string {
  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? '—' : quando.toLocaleDateString('it-IT')
}
